import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  type SnapshotVersion,
  watchPreviewContent,
} from '@tiptap-pro/extension-snapshot'
import { SnapshotCompare } from '@tiptap-pro/extension-snapshot-compare'
import { TiptapCollabProvider } from '@tiptap-pro/provider'
import { Editor, EditorContent, useEditor, useEditorState } from '@tiptap/react'
import { format } from 'date-fns'
import { useTheme } from 'next-themes'
import randomcolor from 'randomcolor'
import { twMerge } from 'tailwind-merge'

import { UserAvatar } from '@/components/common/Avatar'
import Button from '@/components/common/Button'
import { Dialog, DialogContent, DialogTitle } from '@/components/common/Dialog'
import Spinner from '@/components/common/Spinner'
import Switch from '@/components/common/Switch'
import {
  useRichTextAreaContext,
  useVersions,
} from '@/components/common/TextArea/RichTextArea/context'
import { getBaseExtensions } from '@/components/common/TextArea/RichTextArea/hooks'
import type { JSONContent } from '@/components/common/TextArea/RichTextArea/types'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { PROSE_CLASSNAMES } from '@/constants/styles'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationUsers } from '@/hooks/organization/useOrganizationUsers'

interface VersionDialogProps {
  isOpen: boolean
  restoreDisabled?: boolean
  restoreDisabledMessage?: string
  title?: string
  contentClassName?: string
  setIsOpen: (isOpen: boolean) => void
  onClose?: () => void
  onRestore?: (content: JSONContent) => void
  isReadOnly?: boolean
}

interface VersionDialogBaseProps extends VersionDialogProps {
  editor: Editor | null
  provider: TiptapCollabProvider | null
  versions: SnapshotVersion[] | undefined
}

interface VersionItemProps {
  date: number
  title: string
  isActive: boolean
  isSelected: boolean
  onClick: () => void
}

type EditorState = {
  isDiffing: boolean
  users: Set<string>
} | null

const defaultColor = randomcolor({ luminosity: 'light' })
const colorMapping: Map<string, string> = new Map()

const getVersionName = (version: SnapshotVersion) => {
  if (version.name) {
    return version.name
  }
  return `Version ${version.version + 1}`
}

export const VersionItem: React.FC<VersionItemProps> = ({
  title,
  date,
  isActive,
  isSelected,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className={twMerge(
        'flex flex-col gap-y-1 text-start px-[10px] py-2 rounded-md text-sm font-medium text-primary-900 dark:text-primary-100',
        isActive
          ? 'bg-primary-100 dark:bg-primary-800'
          : 'hover:bg-primary-100 dark:hover:bg-primary-800',
      )}
    >
      <span>{title || format(date, 'MMM d, yyyy h:mm a')}</span>
      {isSelected && (
        <span className="text-xs text-primary-500 dark:text-primary-400">
          Current Version
        </span>
      )}
      {title ? (
        <span className="text-xs font-normal text-primary-500 dark:text-primary-400">
          {format(date, 'MMM d, yyyy h:mm a')}
        </span>
      ) : null}
    </button>
  )
}

export const VersionDialogBase: React.FC<VersionDialogBaseProps> = memo(
  ({
    editor: contentEditor,
    provider,
    versions,
    isOpen,
    contentClassName,
    title,
    restoreDisabled: _restoreDisabled,
    restoreDisabledMessage,
    setIsOpen,
    onClose,
    onRestore,
    isReadOnly,
  }) => {
    const { activeOrgId } = useActiveOrgId()
    const { data: organizationUsers } = useOrganizationUsers({
      orgId: activeOrgId,
    })
    const { resolvedTheme } = useTheme()

    const [currentVersionId, setCurrentVersionId] = useState<number | null>(
      null,
    )
    const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [showDiff, setShowDiff] = useState<boolean>(false)

    // this is used to simulate that the restore is in progress, so that we can set a timeout
    // that doesn't confuse the user; we need to set a timeout in handleRestore since without it,
    // it seems like the editor restores to whatever the previously restored value was, rather than
    // the new restored value;
    // TODO: figure out why this is happening
    const [isRestoring, setIsRestoring] = useState<boolean>(false)

    const sidebarRef = useRef<HTMLDivElement>(null)

    const restoreDisabled = useMemo(() => {
      if (_restoreDisabled) {
        return true
      }
      if (versions === undefined) {
        return true
      }
      if (versions.length === 0) {
        return true
      }
      return currentVersionId === versions.at(-1)?.version
    }, [versions, currentVersionId, _restoreDisabled])

    const extensions = getBaseExtensions({
      comment: false,
    })

    const editor = useEditor({
      immediatelyRender: false,
      editable: false,
      content: '',
      extensions: [
        ...extensions,
        SnapshotCompare.configure({
          provider,
        }),
      ],
    })

    const state: EditorState = useEditorState({
      editor,
      selector: (ctx: { editor: Editor | null } | null) => {
        const userHistory:
          | import('@tiptap-pro/extension-snapshot-compare').SnapshotCompareStorage
          | undefined = ctx?.editor?.storage.snapshotCompare
        return {
          isDiffing: userHistory?.isPreviewing ?? false,
          users:
            userHistory?.diffs.reduce<Set<string>>((acc, { attribution }) => {
              const userId = attribution.userId
              return userId ? acc.add(userId) : acc
            }, new Set()) ?? new Set(),
        }
      },
    })

    const reversedVersions = useMemo(
      () => versions?.slice().reverse(),
      [versions],
    )

    const numVersions = Math.max(reversedVersions?.length ?? 0, 1)

    const requestVersion = useCallback(
      (version: number) => {
        if (provider === null) {
          return
        }
        provider.sendStateless(
          JSON.stringify({
            action: 'version.preview',
            version,
          }),
        )
      },
      [provider],
    )

    const handleCompareVersions = useCallback(
      (fromVersion: number, toVersion: number) => {
        editor
          ?.chain()
          .compareVersions({
            toVersion,
            fromVersion,
            /**
             * To add additional data to a user, we  can use this function to add context on a user
             * We've seen two approaches for this, a hash of the user's id mapping to a color
             * Or, to store the user's color preference in a separate field. For simplicity of this example, we just assign a random color
             */
            hydrateUserData: ({ userId }) => {
              if (userId && !colorMapping.has(userId)) {
                colorMapping.set(userId, randomcolor({ luminosity: 'light' }))
              }
              const color = userId
                ? (colorMapping.get(userId) ?? defaultColor)
                : defaultColor
              return {
                /**
                 * this will control the color of the changes made by the user
                 * flexibly allowing you to control the color of the text and background, or even by the type of change
                 */
                color: {
                  // this is the default color for the user
                  backgroundColor:
                    resolvedTheme === 'dark' ? `${color}70` : `${color}B0`,
                  // specifically color deletions as a lighter color
                  delete: {
                    backgroundColor:
                      resolvedTheme === 'dark' ? `${color}40` : `${color}50`,
                  },
                },
              }
            },
            onCompare: (ctx) => {
              if (ctx.error) {
                console.error(ctx.error)
                setErrorMessage(`An error occurred: ${String(ctx.error)}`)
                return
              }
              editor.commands.showDiff(ctx.tr)
            },
          })
          .run()
      },
      [editor, versions, resolvedTheme, setErrorMessage],
    )

    const handleVersionChange = useCallback(
      (newVersion: number) => {
        if (editor?.can().hideDiff()) {
          editor.chain().hideDiff().run()
          setSelectedVersion(null)
        }

        setCurrentVersionId(newVersion)
        requestVersion(newVersion)
        setErrorMessage(null)

        if (newVersion === 0 || !showDiff) {
          return
        }

        handleCompareVersions(newVersion, newVersion - 1)
      },
      [
        editor,
        showDiff,
        handleCompareVersions,
        requestVersion,
        setErrorMessage,
        setCurrentVersionId,
        setSelectedVersion,
      ],
    )

    const onOpenChange = useCallback(
      (open: boolean) => {
        if (!open) {
          onClose?.()
          setIsOpen(false)
          setCurrentVersionId(null)
          editor?.commands.clearContent()
        }
      },
      [editor, setIsOpen, onClose],
    )

    const toggleDiff = useCallback(() => {
      setShowDiff(!showDiff)
      if (currentVersionId === null) {
        return
      }
      if (showDiff) {
        editor?.chain().hideDiff().run()
        requestVersion(currentVersionId)
      } else if (currentVersionId > 0) {
        handleCompareVersions(currentVersionId, currentVersionId - 1)
      }
    }, [
      currentVersionId,
      showDiff,
      editor,
      setShowDiff,
      handleCompareVersions,
      requestVersion,
    ])

    const handleClose = useCallback(() => {
      if (editor?.can().hideDiff) {
        editor.chain().hideDiff().run()
      }
      setIsOpen(false)
      onClose?.()
    }, [editor, setIsOpen, onClose])

    const handleRevert = useCallback(
      (version: number) => {
        const versionData = versions?.find((v) => v.version === version)
        if (!versionData) {
          return
        }
        const versionTitle = getVersionName(versionData)
        if (editor && contentEditor) {
          // TODO: this is a workaround for the revertToVersion not working;
          // i've contacted tiptap support about it (11/10/2024)
          const content = editor.getJSON()
          contentEditor.chain().setContent(content).run()
          setIsRestoring(true)
          setTimeout(() => {
            // TODO: we need to figure out why when we save the new version, the editor storage
            // does not automatically update the versions array, which forces us to force a re-render
            // by either clicking on the editor or clicking another button;
            // closing the dialog is a hacky workaround for this
            contentEditor
              .chain()
              .saveVersion(`Restored from ${versionTitle}`)
              .command(({ editor }) => {
                setIsRestoring(false)
                return true
              })
              .run()
            sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
            if (versions) {
              const newVersion = versions.at(-1)?.version
              if (newVersion !== undefined) {
                // calling handleNewVersion directly causes an error, where tiptap complains
                // that the new version and the old version are the same;
                // TODO: not sure if this is developer error so need to investigate,
                // as this is just a partial workaround for now since it doesn't trigger a new diff
                setCurrentVersionId(newVersion + 1)
              }
            }
          }, 300)
          onRestore?.(content)
        }
      },
      [
        editor,
        contentEditor,
        versions,
        onRestore,
        setIsRestoring,
        setCurrentVersionId,
        sidebarRef.current,
      ],
    )

    const handleRestore = useCallback(() => {
      if (currentVersionId === null) {
        return
      }
      handleRevert(currentVersionId)
    }, [currentVersionId, handleRevert])

    useEffect(() => {
      if (
        isOpen &&
        currentVersionId === null &&
        versions !== undefined &&
        versions.length > 0 &&
        editor
      ) {
        const initialVersion = versions.at(-1)?.version
        if (initialVersion === undefined) {
          return
        }
        handleVersionChange(initialVersion)
      }

      if (!isOpen) {
        setCurrentVersionId(null)
      }
    }, [currentVersionId, versions, isOpen, editor])

    useEffect(() => {
      if (
        isOpen &&
        (versions === undefined || versions.length === 0) &&
        contentEditor &&
        editor
      ) {
        // set the content to the current content of the base editor
        editor.commands.setContent(contentEditor.getJSON())
      }
    }, [isOpen, versions?.length, editor, contentEditor])

    useEffect(() => {
      if (isOpen && provider) {
        const unbindContentWatcher = watchPreviewContent(
          provider,
          (content) => {
            if (editor) {
              editor.commands.setContent(content)
            }
          },
        )
        return () => {
          unbindContentWatcher()
        }
      }
    }, [isOpen, provider, editor])

    if (provider === null) {
      // don't show the dialog until the provider has been enabled;
      // this prevents double rendering since the RichTextArea completely rerenders
      // when we open the dialog (since collaboration is then enabled);
      // TODO: perhaps this is a code smell, and should be refactored
      return null
    }

    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          className="min-w-[95vw] md:max-w-[95vw] min-h-[87vh] p-0"
          aria-describedby={undefined}
        >
          <div className="flex h-[87vh] min-w-[95vw] divide-x divide-primary-200 dark:divide-primary-800">
            <div className="flex flex-col gap-1 w-full h-full max-h-full md:w-2/3 pt-6 pb-5">
              <div className="flex flex-col gap-1">
                <div className="flex items-start justify-between gap-4 w-full min-h-[31px] px-5">
                  <DialogTitle>{title ?? 'Version History'}</DialogTitle>
                  {state?.isDiffing && organizationUsers && (
                    <div className="flex items-center gap-2">
                      {Array.from(state.users).map((userId) => {
                        const user = organizationUsers?.find(
                          (u) => u.id === userId,
                        )
                        const color = colorMapping.get(userId)
                        if (!user) {
                          return null
                        }
                        return (
                          <div
                            key={userId}
                            className="flex items-center justify-center gap-2 whitespace-nowrap w-[30px] h-[30px] mr-10 md:mr-0"
                          >
                            <div className="flex items-center justify-center w-full h-full rounded-full">
                              <div
                                className="flex items-center justify-center w-full h-full rounded-full"
                                style={{
                                  backgroundColor: `${color}`,
                                }}
                              >
                                <UserAvatar
                                  userId={user.id}
                                  width={26}
                                  height={26}
                                />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
                {errorMessage && (
                  <div className="flex items-center px-5">
                    <span className="text-sm text-red-600 dark:text-red-500 mb-2">
                      {errorMessage}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col w-full h-[calc(100%-84px)] mt-1 md:mt-0">
                <div className="h-full overflow-y-auto px-5">
                  <EditorContent
                    editor={editor}
                    className={twMerge(
                      'rich-text-editor-content min-w-full w-full max-w-full max-h-full text-sm',
                      PROSE_CLASSNAMES,
                      'prose-ul:pl-5 prose-ol:m-0 prose-ol:pl-5 prose-li:m-0 prose-li:p-0',
                      contentClassName ?? '',
                    )}
                    aria-hidden={false}
                    data-aria-hidden={false}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 pt-4 px-5 w-full h-11">
                <div>
                  {showDiff &&
                    currentVersionId !== null &&
                    versions &&
                    versions.length > 1 &&
                    versions[currentVersionId - 1] !== undefined && (
                      <span className="min-h-6 flex items-center text-sm text-primary-500 dark:text-primary-400 max-w-[576px]">
                        <span>
                          Comparing&nbsp;
                          <strong className="text-primary-600 dark:text-primary-300">
                            {getVersionName(
                              versions[currentVersionId] ??
                                versions[versions.length - 1],
                            )}
                          </strong>
                          &nbsp;with&nbsp;
                          <strong className="text-primary-600 dark:text-primary-300">
                            {getVersionName(versions[currentVersionId - 1])}
                          </strong>
                        </span>
                      </span>
                    )}
                </div>
                <div className="flex items-center gap-2 h-7">
                  {currentVersionId !== null && currentVersionId > 0 && (
                    <>
                      <span className="text-sm text-primary-500 dark:text-primary-400 whitespace-nowrap">
                        Show diff
                      </span>
                      <Switch checked={showDiff} onClick={toggleDiff} />
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="hidden md:block h-full w-1/3 py-5 px-5">
              <div className="flex flex-col justify-between h-full gap-2">
                <div className="flex flex-col h-[calc(100%-48px)] gap-2">
                  <span className="h-6 text-md font-semibold text-primary-900 dark:text-primary-100">
                    {numVersions} version
                    {numVersions !== 1 ? 's' : ''}
                  </span>
                  <div
                    ref={sidebarRef}
                    className="flex flex-col gap-2 h-[calc(100%-24px)] overflow-y-auto -mx-[10px]"
                  >
                    {reversedVersions && reversedVersions.length > 0 ? (
                      reversedVersions.map((v, idx) => {
                        return (
                          <VersionItem
                            key={idx}
                            date={v.date}
                            title={getVersionName(v)}
                            onClick={() => handleVersionChange(v.version)}
                            isActive={currentVersionId === v.version}
                            isSelected={selectedVersion === v.version}
                          />
                        )
                      })
                    ) : (
                      <div className="flex items-center justify-center h-full w-full text-sm text-primary-500 dark:text-primary-400">
                        No versions yet.
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col justify-center items-center w-full gap-2">
                  <div className="flex items-center justify-between w-full gap-2">
                    <Button
                      type="button"
                      variant="outline-compact"
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                    {!isReadOnly && (
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild={true}>
                          <Button
                            type="button"
                            variant="primary-compact"
                            disabled={restoreDisabled || isRestoring}
                            onClick={handleRestore}
                          >
                            {isRestoring ? (
                              <>
                                <Spinner
                                  className="w-4 h-4"
                                  variant="primary"
                                />
                                Restoring...
                              </>
                            ) : (
                              'Restore'
                            )}
                          </Button>
                        </TooltipTrigger>
                        {restoreDisabled && restoreDisabledMessage && (
                          <TooltipContent className="max-w-[256px]">
                            {restoreDisabledMessage}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  },
)

export const VersionDialog: React.FC<VersionDialogProps> = (props) => {
  const [shouldShow, setShouldShow] = useState<boolean>(props.isOpen)

  const { editor: contentEditor, provider } = useRichTextAreaContext()

  const versions = useVersions()

  useEffect(() => {
    if (!props.isOpen) {
      const timeoutId = setTimeout(() => {
        setShouldShow(false)
        // give time for the dialog to close to prevent flicker
      }, 200)
      return () => clearTimeout(timeoutId)
    } else {
      setShouldShow(true)
    }
  }, [props.isOpen])

  if (!shouldShow || provider === null) {
    // do not render if the dialog is not open or the provider is not yet enabled
    return null
  }

  return (
    <VersionDialogBase
      {...props}
      editor={contentEditor}
      provider={provider}
      versions={versions}
    />
  )
}

VersionDialog.displayName = 'RichTextArea.VersionDialog'
VersionDialogBase.displayName = 'RichTextArea.VersionDialog.Base'
VersionItem.displayName = 'RichTextArea.VersionDialog.Item'
